import { Controller } from '@hotwired/stimulus';
import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.css';
import { jpegFileName } from '../utils/photoCrop';

// Matches ExportListItemsController::ID_CARD_CROP_ASPECT_RATIO - the view
// passes that same number in via data-photo-crop-aspect-ratio-value, so this
// is only the fallback if that attribute is ever missing.
const DEFAULT_ASPECT_RATIO = 0.8;

// An ID card photo doesn't need more than this many pixels on the long
// edge - capping avoids uploading a multi-megapixel crop from a modern phone
// photo. Only ever downscales; a smaller source is left at its own size
// (getCroppedCanvas would otherwise upscale a small crop to fill this,
// blurring it for no benefit).
const MAX_LONG_EDGE = 1600;

// "Student Image" upload on the admin Edit Student / New Student forms
// (export_list_items#create / #update). Some schools' webcams only shoot
// landscape, but this photo feeds ID cards, which want an 8x10-ish portrait
// crop. Rather than silently center-cropping on the server (the old
// behavior - see ExportListItemsController#crop_landscape_non_purchasable_image!,
// which still runs as a fallback if this never fires), show the picked photo
// in an interactive crop box the user can drag/resize, then keep the file
// input's file in sync with the current crop as they adjust it.
//
// The crop is applied eagerly (on the cropper's `ready` and `cropend`
// events) rather than only at form submit, so the preview always shows what
// will upload. But encoding each crop to a file (applyCrop's canvas.toBlob)
// is async, so submitting can still race an in-flight encode - guardSubmit()
// below holds the submit until that settles rather than letting rails-ujs
// pick up a stale file.
//
// Rails performs no image processing (CLAUDE.md) - the crop happens entirely
// client-side via <canvas>; the server just receives an already-cropped file
// like any other upload.
export default class extends Controller {
  static targets = ['input', 'editor', 'image', 'error'];
  static values = { aspectRatio: { type: Number, default: DEFAULT_ASPECT_RATIO } };

  connect() {
    this.cropper = null;
    this.previewUrl = null;
    // Bumped on every applyCrop() call so a slow/delayed toBlob callback from
    // an earlier crop can tell it's stale (the user has since adjusted the
    // box again, or picked a new file) and skip overwriting a newer result.
    this.cropGeneration = 0;
    // canvas.toBlob() is async, so there's a window after every drag/resize
    // where the input still holds the *previous* generation's file. Without
    // guardSubmit() below, clicking "Update Student"/"Add Student" in that
    // window silently uploads the stale crop instead of the one on screen.
    this.cropPending = false;
    this.cropReadyPromise = Promise.resolve();
    // Sole gate on requestSubmit() firing more than once for one held
    // submit - see guardSubmit().
    this.submitQueued = false;
  }

  disconnect() {
    this.destroyCropper();
    this.revokePreviewUrl();
  }

  selectFile() {
    const file = this.inputTarget.files[0];

    this.destroyCropper();
    this.revokePreviewUrl();
    if (this.hasErrorTarget) this.errorTarget.hidden = true;

    if (!file) {
      this.editorTarget.hidden = true;
      return;
    }

    this.previewUrl = URL.createObjectURL(file);
    this.imageTarget.onerror = () => this.showLoadError();
    this.imageTarget.onload = () => {
      this.cropper = new Cropper(this.imageTarget, {
        aspectRatio: this.aspectRatioValue,
        viewMode: 1,
        autoCropArea: 1,
        background: false,
        responsive: true,
        ready: () => this.applyCrop(),
        cropend: () => this.applyCrop(),
      });
    };
    this.imageTarget.src = this.previewUrl;
    this.editorTarget.hidden = false;
  }

  resetCrop() {
    if (!this.cropper) return;

    this.cropper.reset();
    this.applyCrop();
  }

  // Guards the form's submit against the async gap in applyCrop() below: if
  // the user clicks "Update Student"/"Add Student" while the latest crop's
  // toBlob() is still encoding, hold the submit and re-fire it once that
  // file has actually landed in the input, instead of letting rails-ujs pick
  // up whatever (possibly stale) file was already there.
  guardSubmit(event) {
    if (!this.cropPending) return;

    event.preventDefault();
    event.stopPropagation();

    // Without this, a second click (or Enter) before the held submit fires
    // attaches its own .then() to the same promise, so both resolve
    // together and the form submits twice.
    if (this.submitQueued) return;
    this.submitQueued = true;

    const { submitter } = event;
    this.cropReadyPromise.then(() => {
      this.submitQueued = false;
      this.element.requestSubmit(submitter);
    });
  }

  applyCrop() {
    const cropOptions = { imageSmoothingQuality: 'high' };
    // aspectRatio is width/height and is always < 1 (a portrait ID card
    // crop), so height - not width - is the long edge to cap.
    const cropData = this.cropper.getData();
    if (cropData.height > MAX_LONG_EDGE) {
      cropOptions.height = MAX_LONG_EDGE;
      cropOptions.width = Math.round(MAX_LONG_EDGE * this.aspectRatioValue);
    }

    const canvas = this.cropper.getCroppedCanvas(cropOptions);
    if (!canvas) return;

    const fileName = jpegFileName(this.inputTarget.files[0]?.name);
    this.cropGeneration += 1;
    const generation = this.cropGeneration;

    // A submit already held on the crop this call supersedes must not be
    // dropped: resolve it now so guardSubmit's requestSubmit() fires, comes
    // back through guardSubmit, and waits on the promise created below
    // instead. Its own generation's toBlob callback (further down) will
    // see it's stale and skip re-resolving it.
    if (this.resolveCropReady) this.resolveCropReady();

    this.cropPending = true;
    this.cropReadyPromise = new Promise((resolve) => { this.resolveCropReady = resolve; });

    canvas.toBlob((blob) => {
      // A newer applyCrop() call already owns cropPending/cropReadyPromise -
      // this stale callback finishing late must not resolve it early.
      if (generation !== this.cropGeneration) return;

      if (blob) {
        const file = new File([blob], fileName, { type: 'image/jpeg' });
        const transfer = new DataTransfer();
        transfer.items.add(file);
        this.inputTarget.files = transfer.files;
      }

      this.cropPending = false;
      this.resolveCropReady();
    }, 'image/jpeg', 0.92);
  }

  showLoadError() {
    this.destroyCropper();
    this.editorTarget.hidden = true;
    if (this.hasErrorTarget) this.errorTarget.hidden = false;
  }

  destroyCropper() {
    if (this.cropper) {
      this.cropper.destroy();
      this.cropper = null;
    }
  }

  revokePreviewUrl() {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    }
  }
}
